import React, { Component } from 'react'
import cytoscape from 'cytoscape'
import dagre from 'cytoscape-dagre'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { updateAction } from 'src/store/modules/runbooks/actions'

cytoscape.use(dagre)

let cytoscapeRootStyle = {
    height: '800px',
    width: '100%',
    display: 'block',
    left: '100px',
    top: '50px',
    zIndex: '11',
}

const layout = {
    name: 'dagre',
    fit: true,
    nodeDimensionsIncludeLabels: true,
    padding: 120,
    spacingFactor: 1,
}

const setEquals = (as, bs) => {
    if (as.size !== bs.size) return false
    for (const a of as) if (!bs.has(a)) return false
    return true
}

class DirectedGraph extends Component {
    static propTypes = {
        nodes: PropTypes.arrayOf(PropTypes.object).isRequired,
        edges: PropTypes.arrayOf(PropTypes.object).isRequired,
        style: PropTypes.array.isRequired,
        onNodeClick: PropTypes.func,
    }

    componentDidMount() {
        const { updateAction } = this.props.actions
        const { nodes, edges, onNodeClick, runbookId } = this.props
        const config = {
            container: this.refs.cytoscapeRoot,
            boxSelectionEnabled: false,
            autounselectify: true,
            minZoom: 0.1,
            maxZoom: 10,
            pan: { x: 1, y: 1 },
            zoom: 1,
            layout: layout,
            style: DirectedGraph.getStyle(this.props),
        }

        this.cy = cytoscape(config)
        this.runLayout(nodes, edges)
        this.cy.fit()

        // Save the initial positions to the Redux Store
        // const cynodesPositions = this.cy.nodes()

        // cynodesPositions.forEach(node => {
        //     updateAction(
        //         runbookId,
        //         node.data().id,
        //         {
        //             positionX: Math.round(node.position().x),
        //             positionY: Math.round(node.position().y)
        //         })
        // });

        this.cy.on('tap', 'node', (evt) => {
            const node = evt.target
            return onNodeClick ? onNodeClick(node.data()) : null
        })

        // Trigger an event when performing a dragmotion
        this.cy.on('tapend ', 'node', (evt) => {
            const node = evt.target
            updateAction(runbookId, node.data().id, {
                positionX: Math.round(node.position().x),
                positionY: Math.round(node.position().y),
            })
        })
    }

    static getStyle(props) {
        return props.style
    }

    cy = null

    runLayout(nodes, edges) {
        this.cy.elements().remove()
        const { runbookId } = this.props
        // Devide the nodes and the edges
        const autodrawnNodes = []
        const positionNodes = []
        const autodrawnEdges = []
        const positionEdges = []

        nodes.forEach((node) => {
            if ('position' in node) {
                positionNodes.push(node)
            } else {
                autodrawnNodes.push(node)
            }
        })

        const autodrawnNodesID = []

        autodrawnNodes.forEach((node) => {
            autodrawnNodesID.push(node.data.id)
        })

        edges.forEach((edge) => {
            if (
                autodrawnNodesID.includes(edge.data.target) &&
                autodrawnNodesID.includes(edge.data.source)
            ) {
                autodrawnEdges.push(edge)
            } else {
                positionEdges.push(edge)
            }
        })

        // Add only the nodes that do not have a position
        const autoDrawndict = { nodes: autodrawnNodes, edges: autodrawnEdges }
        this.cy.add(autoDrawndict)
        this.cy.layout(layout).run()

        // Determine the furthest new node that has been drawn
        var cynodes = this.cy.nodes()
        var furthestXCoordinate = 0
        cynodes.forEach((node) => {
            if (furthestXCoordinate < node.position().x) {
                furthestXCoordinate = node.position().x
            }
        })

        // Determine the most negative coordinate
        var negativeXCoordinate = 0
        positionNodes.forEach((node) => {
            if (node.position.x < negativeXCoordinate) {
                negativeXCoordinate = node.position.x
            }
        })

        // Move the position nodes to the new position
        negativeXCoordinate *= -1
        if (!(autodrawnNodesID.length === 0)) {
            positionNodes.map(
                (node) =>
                    (node.position.x =
                        node.position.x +
                        furthestXCoordinate +
                        negativeXCoordinate +
                        80)
            )
        }

        // Add the nodes with a position afterwards
        const positionDrawnDict = { nodes: positionNodes, edges: positionEdges }
        this.cy.add(positionDrawnDict)

        const cynodesPositions = this.cy.nodes()

        cynodesPositions.forEach((node) => {
            this.props.actions.updateAction(runbookId, node.data().id, {
                positionX: node.position().x,
                positionY: node.position().y,
            })
        })
    }

    shouldComponentUpdate() {
        return false
    }

    isStyleChanged(nextProps) {
        /**
         * Go through style properties and check if one is changed
         * Since most of them are functions, we use toString()
         * to check the equality of the definitions
         */
        const { style } = this.props
        const { nextStyle } = nextProps
        return !isEqual(style, nextStyle)
    }

    areElementsChanged(nextProps) {
        const { nodes, edges } = this.props

        const { nodes: nextNodes, edges: nextEdges } = nextProps

        if (
            nodes.length !== nextNodes.length ||
            edges.length !== nextEdges.length
        ) {
            return true
        }

        const nodeIds = nodes.map((node) => node.data.id)
        const nextNodeIds = nextNodes.map((node) => node.data.id)
        if (!setEquals(new Set(nodeIds), new Set(nextNodeIds))) {
            return true
        }

        // there are not multiple edges between source and target
        const edgeIds = edges.map(
            (edge) => `${edge.data.source}--${edge.data.target}`
        )
        const nextEdgeIds = nextEdges.map(
            (edge) => `${edge.data.source}--${edge.data.target}`
        )
        if (!setEquals(new Set(edgeIds), new Set(nextEdgeIds))) {
            return true
        }

        return false
    }
    componentWillReceiveProps(nextProps) {
        const { nodes, edges } = this.props

        const { nodes: nextNodes, edges: nextEdges } = nextProps

        if (this.areElementsChanged(nextProps)) {
            // if the nodes are changed in any way, the layout needs to run again
            this.runLayout(nextNodes, nextEdges)
        }
        if (nodes.length === nextNodes.length) {
            nextNodes.forEach((nextNode) => {
                const { id } = nextNode.data

                this.cy.$(`#${id}`).data(nextNode.data)
                this.cy.$(`#${id}`).position()
            })
        }

        if (edges.length === nextEdges.length) {
            nextEdges.forEach((nextEdge) => {
                const { source, target } = nextEdge.data
                this.cy
                    .$(`edge[source="${source}"][target="${target}"]`)
                    .data(nextEdge.data)
            })
        }

        if (this.isStyleChanged(nextProps) || true) {
            this.cy.style(DirectedGraph.getStyle(nextProps))
        }
    }

    componentWillUnmount() {
        this.cy.destroy()
    }

    render() {
        return <div style={cytoscapeRootStyle} ref="cytoscapeRoot" />
    }
}

// Update node position action
const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                updateAction,
            },
            dispatch
        ),
    }
}

export default connect(null, mapDispatchToProps)(DirectedGraph)
// export default DirectedGraph;
